/**
 * CircleObstructionDrawer Component
 * Interactive circle drawing tool for circular obstructions
 *
 * Extracted from GeoTiffViewerEmbedded.tsx (lines 505-626)
 * Two-click interaction: first click sets center, second click confirms radius
 */

import React, { useState, useEffect } from 'react';
import { useMap } from 'react-leaflet';
import { CircleMarker, Polygon } from 'react-leaflet';

export interface CircleObstructionDrawerProps {
  isDrawing: boolean;
  center: [number, number] | null;
  radius: number;
  onSetCenter: (latlng: [number, number]) => void;
  onSetRadius: (radius: number) => void;
  onFinish: () => void;
  onCancel: () => void;
}

/**
 * Interactive circle obstruction drawing component
 * Red color scheme with center marker and radius preview
 * ESC key cancels drawing
 */
export function CircleObstructionDrawer({
  isDrawing,
  center,
  radius,
  onSetCenter,
  onSetRadius,
  onFinish,
  onCancel,
}: CircleObstructionDrawerProps) {
  const map = useMap();
  const [cursorPos, setCursorPos] = useState<[number, number] | null>(null);

  useEffect(() => {
    if (!isDrawing) {
      setCursorPos(null);
      return;
    }

    const handleClick = (e: any) => {
      e.originalEvent.preventDefault();
      e.originalEvent.stopPropagation();

      if (!center) {
        // First click: set center
        onSetCenter([e.latlng.lat, e.latlng.lng]);
      } else {
        // Second click: finish circle
        onFinish();
      }
    };

    const handleMouseMove = (e: any) => {
      setCursorPos([e.latlng.lat, e.latlng.lng]);

      // Calculate radius if center is set
      if (center) {
        const dLat = e.latlng.lat - center[0];
        const dLng = e.latlng.lng - center[1];
        const metersPerDegreeLat = 111320;
        const metersPerDegreeLng = 111320 * Math.cos(center[0] * Math.PI / 180);
        const dy = dLat * metersPerDegreeLat;
        const dx = dLng * metersPerDegreeLng;
        const newRadius = Math.sqrt(dx * dx + dy * dy);
        onSetRadius(newRadius);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancel();
      }
    };

    map.on('click', handleClick);
    map.on('mousemove', handleMouseMove);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      map.off('click', handleClick);
      map.off('mousemove', handleMouseMove);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isDrawing, map, center, onSetCenter, onSetRadius, onFinish, onCancel]);

  if (!isDrawing) return null;

  // Convert circle to polygon points for preview
  const circleToPolygon = (centerPoint: [number, number], radiusMeters: number): [number, number][] => {
    const segments = 32;
    const vertices: [number, number][] = [];
    const metersPerDegreeLat = 111320;
    const metersPerDegreeLng = 111320 * Math.cos(centerPoint[0] * Math.PI / 180);

    for (let i = 0; i < segments; i++) {
      const angle = (i / segments) * 2 * Math.PI;
      const dLat = (Math.cos(angle) * radiusMeters) / metersPerDegreeLat;
      const dLng = (Math.sin(angle) * radiusMeters) / metersPerDegreeLng;
      vertices.push([centerPoint[0] + dLat, centerPoint[1] + dLng]);
    }
    return vertices;
  };

  return (
    <>
      {/* Center marker */}
      {center && (
        <CircleMarker
          center={center}
          // @ts-ignore - react-leaflet v5 supports radius but v2 types don't
          radius={6}
          pathOptions={{
            color: '#dc2626',
            fillColor: '#dc2626',
            fillOpacity: 1,
            weight: 2,
          }}
        />
      )}

      {/* Circle preview */}
      {center && radius > 0 && (
        <Polygon
          positions={circleToPolygon(center, radius)}
          pathOptions={{
            color: '#dc2626',
            fillColor: '#fca5a5',
            fillOpacity: 0.3,
            weight: 2,
            dashArray: '5, 5',
          }}
        />
      )}
    </>
  );
}
